import { useCallback, useEffect, useRef } from 'react';
import { emitir, enviar } from './senales.js';

/** El estándar de la industria para una impresión visible: la mitad del aviso, un segundo. */
const AREA_VISIBLE = 0.5;
const TIEMPO_VISIBLE_MS = 1_000;

/**
 * Impresión y clic de un aviso: `aviso_visto` y `aviso_clic`, de donde sale el CTR del tablero.
 *
 * Como `pagina_vista`, faltaron desde que salió el módulo: la tabla de avisos del panel mostraba
 * ceros sin ningún error. Ver `useSenalDePagina`.
 *
 * **Visto** es la mitad del aviso a la vista durante un segundo seguido, con la pestaña visible, y
 * cuenta una vez por aviso y por montaje: un aviso que entra y sale de pantalla al scrollear no
 * suma una impresión por pasada.
 *
 * **Clic** en un aviso de imagen es el clic en su link. En un snippet HTML el clic pasa adentro de
 * un iframe aislado (sin `allow-same-origin`) y desde acá no se ve: lo único observable es que la
 * ventana pierde el foco **hacia ese iframe**. Es la técnica habitual y es aproximada: un lector
 * que tabula dentro del iframe también cuenta. Se toma una vez por foco.
 */
export function useSenalesDeAviso(adId: string | undefined): {
  ref: (el: HTMLElement | null) => void;
  iframeRef: (el: HTMLIFrameElement | null) => void;
  alClic: () => void;
} {
  const nodo = useRef<HTMLElement | null>(null);
  const iframe = useRef<HTMLIFrameElement | null>(null);
  const visto = useRef<string | null>(null);

  const alClic = useCallback(() => {
    if (!adId) return;
    emitir({ tipo: 'aviso_clic', adId });
    // El link abre otra pestaña y esta puede quedar en segundo plano mucho tiempo: se manda ya.
    enviar();
  }, [adId]);

  // Impresión visible.
  useEffect(() => {
    const el = nodo.current;
    if (!adId || !el || typeof IntersectionObserver === 'undefined') return;
    if (visto.current === adId) return;

    let temporizador: number | undefined;
    const cancelar = () => {
      if (temporizador !== undefined) window.clearTimeout(temporizador);
      temporizador = undefined;
    };

    const observador = new IntersectionObserver(
      ([entrada]) => {
        if (!entrada?.isIntersecting || entrada.intersectionRatio < AREA_VISIBLE) {
          cancelar();
          return;
        }
        if (temporizador !== undefined) return;
        temporizador = window.setTimeout(() => {
          temporizador = undefined;
          if (document.visibilityState !== 'visible' || visto.current === adId) return;
          visto.current = adId;
          emitir({ tipo: 'aviso_visto', adId });
          observador.disconnect();
        }, TIEMPO_VISIBLE_MS);
      },
      { threshold: [0, AREA_VISIBLE] },
    );
    observador.observe(el);

    return () => {
      cancelar();
      observador.disconnect();
    };
  }, [adId]);

  // Clic dentro del iframe de un snippet.
  useEffect(() => {
    if (!adId || typeof window === 'undefined') return;
    const alPerderFoco = () => {
      // El foco se mueve después del `blur`: se mira en la vuelta siguiente del event loop.
      window.setTimeout(() => {
        if (iframe.current && document.activeElement === iframe.current) alClic();
      }, 0);
    };
    window.addEventListener('blur', alPerderFoco);
    return () => window.removeEventListener('blur', alPerderFoco);
  }, [adId, alClic]);

  const ref = useCallback((el: HTMLElement | null) => {
    nodo.current = el;
  }, []);
  const iframeRef = useCallback((el: HTMLIFrameElement | null) => {
    iframe.current = el;
  }, []);

  return { ref, iframeRef, alClic };
}
